
import CryptoJS from 'crypto-js';

// Chave LGPD do ambiente
const LGPD_KEY = process.env.REACT_APP_LGPD_KEY || '9wcC2yq84YQd';

if (!LGPD_KEY) {
  console.warn('⚠️ LGPD_KEY não está definida nas variáveis de ambiente. Usando chave padrão temporária.');
}

if (LGPD_KEY.length < 8) {
  console.warn('⚠️ LGPD_KEY deve ter pelo menos 8 caracteres. Usando chave padrão temporária.');
}

/**
 * Criptografa um texto usando uma abordagem compacta baseada em substituição
 * @param {string} texto - Texto a ser criptografado
 * @returns {string} - Texto criptografado (tamanho similar ao original)
 */
function criptografar(texto) {
  if (!texto) return texto;
  try {
    // Gera uma chave derivada da LGPD_KEY
    const keyHash = CryptoJS.SHA256(LGPD_KEY).toString();
    
    // Converte o texto para base64 primeiro
    const textoBase64 = btoa(texto);
    
    // Aplica uma transformação simples usando a chave
    let resultado = '';
    for (let i = 0; i < textoBase64.length; i++) {
      const charCode = textoBase64.charCodeAt(i);
      const keyChar = keyHash.charCodeAt(i % keyHash.length);
      const novoCharCode = (charCode + keyChar) % 256;
      resultado += String.fromCharCode(novoCharCode);
    }
    
    // Adiciona um prefixo para identificar que está criptografado
    return 'ENC_' + resultado;
  } catch (error) {
    console.error('Erro ao criptografar:', error);
    return texto; // Retorna o texto original em caso de erro
  }
}

/**
 * Descriptografa um texto usando a mesma abordagem
 * @param {string} textoCriptografado - Texto criptografado
 * @returns {string} - Texto descriptografado
 */
function descriptografar(textoCriptografado) {
  if (!textoCriptografado) return textoCriptografado;
  try {
    // Verifica se tem o prefixo de criptografia
    if (!textoCriptografado.startsWith('ENC_')) {
      return textoCriptografado; // Retorna como está se não estiver criptografado
    }
    
    // Remove o prefixo
    const textoCript = textoCriptografado.substring(4);
    
    // Gera a mesma chave derivada
    const keyHash = CryptoJS.SHA256(LGPD_KEY).toString();
    
    // Reverte a transformação
    let resultado = '';
    for (let i = 0; i < textoCript.length; i++) {
      const charCode = textoCript.charCodeAt(i);
      const keyChar = keyHash.charCodeAt(i % keyHash.length);
      const novoCharCode = (charCode - keyChar + 256) % 256;
      resultado += String.fromCharCode(novoCharCode);
    }
    
    // Converte de base64 de volta para string
    return atob(resultado);
  } catch (error) {
    console.error('Erro ao descriptografar:', error);
    return textoCriptografado; // Retorna o texto original em caso de erro
  }
}

/**
 * Mascara um email mantendo apenas os primeiros caracteres visíveis
 * @param {string} email - Email a ser mascarado
 * @returns {string} - Email mascarado
 */
function mascararEmail(email) {
  if (!email || !email.includes('@')) return email;
  
  const [localPart, domain] = email.split('@');
  
  if (localPart.length <= 2) {
    return `${localPart}***@${domain}`;
  }
  
  const visibleChars = Math.min(2, localPart.length);
  const maskedPart = localPart.substring(0, visibleChars) + '***';
  
  return `${maskedPart}@${domain}`;
}

/**
 * Mascara um CPF mantendo apenas os primeiros e últimos dígitos
 * @param {string} cpf - CPF a ser mascarado
 * @returns {string} - CPF mascarado
 */
function mascararCPF(cpf) {
  if (!cpf) return cpf;
  
  const cpfLimpo = cpf.replace(/\D/g, '');
  if (cpfLimpo.length !== 11) return cpf;
  
  return `${cpfLimpo.substring(0, 3)}.***.***-${cpfLimpo.substring(9)}`;
}

/**
 * Mascara um CNPJ mantendo apenas os primeiros e últimos dígitos
 * @param {string} cnpj - CNPJ a ser mascarado
 * @returns {string} - CNPJ mascarado
 */
function mascararCNPJ(cnpj) {
  if (!cnpj) return cnpj;
  
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  if (cnpjLimpo.length !== 14) return cnpj;
  
  return `${cnpjLimpo.substring(0, 2)}.***.***/****-${cnpjLimpo.substring(12)}`;
}

/**
 * Mascara um telefone mantendo apenas os primeiros e últimos dígitos
 * @param {string} telefone - Telefone a ser mascarado
 * @returns {string} - Telefone mascarado
 */
function mascararTelefone(telefone) {
  if (!telefone) return telefone;
  
  const telefoneLimpo = telefone.replace(/\D/g, '');
  if (telefoneLimpo.length < 10) return telefone;
  
  const visibleStart = telefoneLimpo.substring(0, 2);
  const visibleEnd = telefoneLimpo.substring(telefoneLimpo.length - 2);
  const maskedMiddle = '*'.repeat(telefoneLimpo.length - 4);
  
  return `(${visibleStart}) ${maskedMiddle}-${visibleEnd}`;
}

/**
 * Mascara dados bancários (conta e agência)
 * @param {string} valor - Valor a ser mascarado
 * @returns {string} - Valor mascarado
 */
function mascararDadosBancarios(valor) {
  if (!valor) return valor;
  
  const valorLimpo = valor.replace(/\D/g, '');
  if (valorLimpo.length < 3) return valor;
  
  const visibleStart = valorLimpo.substring(0, 1);
  const visibleEnd = valorLimpo.substring(valorLimpo.length - 1);
  const maskedMiddle = '*'.repeat(valorLimpo.length - 2);
  
  return `${visibleStart}${maskedMiddle}${visibleEnd}`;
}

/**
 * Verifica se um texto está criptografado
 * @param {string} texto - Texto a verificar
 * @returns {boolean} - True se estiver criptografado
 */
function estaCriptografado(texto) {
  if (!texto) return false;
  // Verifica se tem o prefixo de criptografia
  return texto.startsWith('ENC_');
}

/**
 * Aplica mascaramento automático baseado no tipo de dado
 * @param {string} valor - Valor a ser mascarado
 * @param {string} tipo - Tipo do dado (cpf, cnpj, email, telefone, banco)
 * @returns {string} - Valor mascarado
 */
function mascararPorTipo(valor, tipo) {
  if (!valor) return valor;
  
  switch (tipo.toLowerCase()) {
    case 'cpf':
      return mascararCPF(valor);
    case 'cnpj':
      return mascararCNPJ(valor);
    case 'email':
      return mascararEmail(valor);
    case 'telefone':
      return mascararTelefone(valor);
    case 'banco':
      return mascararDadosBancarios(valor);
    default:
      return valor;
  }
}

// ============================================================================
// FUNÇÕES DE FORMATAÇÃO LGPD
// ============================================================================

/**
 * Formata um CPF (000.000.000-00)
 * @param {string} cpf - CPF a ser formatado
 * @returns {string} CPF formatado
 */
function formatarCPF(cpf) {
  if (!cpf) return '';
  const numeros = cpf.replace(/\D/g, '');
  return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Formata um CNPJ (00.000.000/0000-00)
 * @param {string} cnpj - CNPJ a ser formatado
 * @returns {string} CNPJ formatado
 */
function formatarCNPJ(cnpj) {
  if (!cnpj) return '';
  const numeros = cnpj.replace(/\D/g, '');
  return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/**
 * Formata um CPF ou CNPJ automaticamente baseado no tamanho
 * @param {string} valor - CPF ou CNPJ a ser formatado
 * @returns {string} Valor formatado
 */
function formatarCPFCNPJ(valor) {
  if (!valor) return '';
  const numeros = valor.replace(/\D/g, '');
  return numeros.length <= 11 ? formatarCPF(numeros) : formatarCNPJ(numeros);
}

/**
 * Formata um número de telefone ((00) 00000-0000)
 * @param {string} telefone - Telefone a ser formatado
 * @returns {string} Telefone formatado
 */
function formatarTelefone(telefone) {
  if (!telefone) return '';
  const numeros = telefone.replace(/\D/g, '');
  if (numeros.length <= 10) {
    return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
}

/**
 * Formata CPF com mascaramento LGPD (123.***.***-45)
 * @param {string} cpf - CPF a ser formatado e mascarado
 * @param {boolean} mascarar - Se deve aplicar mascaramento (padrão: true)
 * @returns {string} CPF formatado e mascarado
 */
export const formatarCPFLGPD = (cpf, mascarar = true) => {
  if (!cpf) return '';
  
  // Se estiver criptografado, descriptografa primeiro
  if (estaCriptografado(cpf)) {
    cpf = descriptografar(cpf);
  }
  
  if (mascarar) {
    return mascararCPF(cpf);
  }
  
  return formatarCPF(cpf);
};

/**
 * Formata CNPJ com mascaramento LGPD. Exemplo: 12.xxx.xxx/xxxx-34
 * @param {string} cnpj - CNPJ a ser formatado e mascarado
 * @param {boolean} mascarar - Se deve aplicar mascaramento (padrão: true)
 * @returns {string} CNPJ formatado e mascarado
 */
export const formatarCNPJLGPD = (cnpj, mascarar = true) => {
  if (!cnpj) return '';
  
  // Se estiver criptografado, descriptografa primeiro
  if (estaCriptografado(cnpj)) {
    cnpj = descriptografar(cnpj);
  }
  
  if (mascarar) {
    return mascararCNPJ(cnpj);
  }
  
  return formatarCNPJ(cnpj);
};

/**
 * Formata CPF/CNPJ automaticamente com mascaramento LGPD
 * @param {string} valor - CPF ou CNPJ a ser formatado
 * @param {string} tipo - Tipo de pessoa ('Física' ou 'Jurídica')
 * @param {boolean} mascarar - Se deve aplicar mascaramento (padrão: true)
 * @returns {string} Valor formatado e mascarado
 */
export const formatarCPFCNPJLGPD = (valor, tipo = 'Física', mascarar = true) => {
  if (!valor) return '';
  
  // Se estiver criptografado, descriptografa primeiro
  if (estaCriptografado(valor)) {
    valor = descriptografar(valor);
  }
  
  if (mascarar) {
    return tipo === 'Física' ? mascararCPF(valor) : mascararCNPJ(valor);
  }
  
  return formatarCPFCNPJ(valor);
};

/**
 * Formata telefone com mascaramento LGPD ((11) ****-89)
 * @param {string} telefone - Telefone a ser formatado e mascarado
 * @param {boolean} mascarar - Se deve aplicar mascaramento (padrão: true)
 * @returns {string} Telefone formatado e mascarado
 */
export const formatarTelefoneLGPD = (telefone, mascarar = true) => {
  if (!telefone) return '';
  
  // Se estiver criptografado, descriptografa primeiro
  if (estaCriptografado(telefone)) {
    telefone = descriptografar(telefone);
  }
  
  if (mascarar) {
    return mascararTelefone(telefone);
  }
  
  return formatarTelefone(telefone);
};

/**
 * Formata email com mascaramento LGPD (us***@email.com)
 * @param {string} email - Email a ser mascarado
 * @param {boolean} mascarar - Se deve aplicar mascaramento (padrão: true)
 * @returns {string} Email mascarado
 */
export const formatarEmailLGPD = (email, mascarar = true) => {
  if (!email) return '';
  
  // Se estiver criptografado, descriptografa primeiro
  if (estaCriptografado(email)) {
    email = descriptografar(email);
  }
  
  if (mascarar) {
    return mascararEmail(email);
  }
  
  return email;
};

/**
 * Formata dados bancários com mascaramento LGPD (1****9)
 * @param {string} valor - Dados bancários a serem mascarados
 * @param {boolean} mascarar - Se deve aplicar mascaramento (padrão: true)
 * @returns {string} Dados bancários mascarados
 */
export const formatarDadosBancariosLGPD = (valor, mascarar = true) => {
  if (!valor) return '';
  
  // Se estiver criptografado, descriptografa primeiro
  if (estaCriptografado(valor)) {
    valor = descriptografar(valor);
  }
  
  if (mascarar) {
    return mascararDadosBancarios(valor);
  }
  
  return valor;
};

/**
 * Criptografa dados sensíveis antes de salvar no banco
 * @param {string} valor - Valor a ser criptografado
 * @returns {string} Valor criptografado
 */
export const criptografarDados = (valor) => {
  if (!valor) return valor;
  return criptografar(valor);
};

/**
 * Descriptografa dados sensíveis antes de exibir
 * @param {string} valor - Valor criptografado
 * @returns {string} Valor descriptografado
 */
export const descriptografarDados = (valor) => {
  if (!valor) return valor;
  
  if (estaCriptografado(valor)) {
    return descriptografar(valor);
  }
  
  return valor;
};

/**
 * Aplica mascaramento automático baseado no tipo de dado
 * @param {string} valor - Valor a ser mascarado
 * @param {string} tipo - Tipo do dado (cpf, cnpj, email, telefone, banco)
 * @param {boolean} descriptografar - Se deve descriptografar primeiro (padrão: true)
 * @returns {string} Valor mascarado
 */
export const mascararDadosPorTipo = (valor, tipo, descriptografar = true) => {
  if (!valor) return valor;
  
  let valorProcessado = valor;
  
  if (descriptografar && estaCriptografado(valor)) {
    valorProcessado = descriptografar(valor);
  }
  
  return mascararPorTipo(valorProcessado, tipo);
};

/**
 * Verifica se um valor está criptografado
 * @param {string} valor - Valor a verificar
 * @returns {boolean} True se estiver criptografado
 */
export const verificarCriptografia = (valor) => {
  return estaCriptografado(valor);
};

export {
  criptografar,
  descriptografar,
  mascararEmail,
  mascararCPF,
  mascararCNPJ,
  mascararTelefone,
  mascararDadosBancarios,
  mascararPorTipo,
  estaCriptografado,
  formatarCPFCNPJ
}; 